import {
  faArrowDownToLine,
  faArrowsRetweet,
  faBlockBrick,
  faCirclePlus,
  faClipboard,
  faCube,
  faGlobe,
  faPaintbrush,
  faPersonRunning,
} from "@fortawesome/pro-regular-svg-icons";
import {
  faFolderArrowDown,
  faLaptopFile,
} from "@fortawesome/pro-solid-svg-icons";
import { Stack } from "@mui/material";
import { capitalize } from "lodash";
import { useState } from "react";

import { ExportDialog } from "@/components/ExportDialog";
import {
  AudioIcon,
  CanvasIcon,
  PromptEditIcon,
  ReframeIcon,
  RemoveBackgroundIcon,
  ThreeDIcon,
  TwoDIcon,
  UpscaleImageIcon,
  UpscaleVideoIcon,
  VectorizeIcon,
  VideoIcon,
} from "@/components/ui-v2/icons";
import DriveManagerDialog from "@/features/drive/DriveManagerDialog";
import { useAvailableActionIds } from "@/features/files/hooks/useAvailableActionIds";
import { useAvailableExportOptionIds } from "@/features/files/hooks/useAvailableExportOptionIds";
import { ActionId, ExportOptionId } from "@/features/files/types";
import { isRasterImage, isVideo } from "@/features/files/utils";
import {
  useActivateInferenceForm,
  useActivateReforgeForm,
  useBasicData,
  useCopyFileLink,
  useCopyFilesToClipboard,
  useDownloadFilesAsObj,
  useEditInCanvas,
  useSaveToDrive,
} from "@/hooks";
import { EditInCanvasObject } from "@/hooks/fileImageActions/useEditInCanvas";
import { useDownloadObjects } from "@/hooks/useDownloadObjects";
import { useExportObjects } from "@/hooks/useExportObjects";
import { getObjectType } from "@/utils/libraryObjectUtils";

import { ButtonGroup, Icon } from "../ui-v2";
import {
  SelectionMenuItem,
  SelectionSidebarMainActionButton,
} from "./SelectionSidebarMainActionButton";
import { SelectedObject, SidebarContext } from "./types";

const isFileOrAsset = (
  o: SelectedObject,
): o is Extract<SelectedObject, { __typename: "File" | "Asset" }> =>
  o.__typename === "File" || o.__typename === "Asset";

type SelectionSidebarMainActionsProps = {
  context: SidebarContext;
  objects: SelectedObject[];
  sessionId?: string;
};

export const SelectionSidebarMainActions = (
  props: SelectionSidebarMainActionsProps,
) => {
  const { objects, sessionId, context } = props;

  const { workspace } = useBasicData();
  const availableActionIds = useAvailableActionIds({
    objects,
    pageContext: context,
  });
  const availableExportOptionIds = useAvailableExportOptionIds({
    items: objects,
  });

  if (availableExportOptionIds.size === 0) {
    availableActionIds.delete("EXPORT");
  }

  const [isDriveManagerDialogOpen, setIsDriveManagerDialogOpen] =
    useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  // File Reference Creation Actions
  const { editInCanvas } = useEditInCanvas();
  const { activateInferenceForm } = useActivateInferenceForm({ sessionId });
  const { activateReforgeForm } = useActivateReforgeForm({
    sessionId,
    workspace: workspace ?? undefined,
  });

  // File Refinement actions
  const allFiles = objects.filter((o) => o.__typename === "File");
  const filesAndAssets = objects.filter(isFileOrAsset);
  const singleFile = objects.find(
    (o): o is Extract<SelectedObject, { __typename: "File" }> =>
      o.__typename === "File",
  );
  const singleFileOrAsset =
    filesAndAssets.length > 0 ? filesAndAssets[0] : null;

  // File Export actions
  const { saveToDrive } = useSaveToDrive();
  const { downloadObjects } = useDownloadObjects();
  const { canExportObjects, exportObjects } = useExportObjects();
  const { downloadFilesAsObj } = useDownloadFilesAsObj();
  const { copyFilesToClipboard } = useCopyFilesToClipboard();
  const { copyFileLink } = useCopyFileLink();

  const itemType =
    singleFile?.__typename === "File" ? getObjectType(singleFile) : "item";

  const handleDownloadFiles = async (
    selectedObjects: Extract<
      SelectedObject,
      { __typename: "File" | "Asset" }
    >[],
  ) => {
    if (canExportObjects(selectedObjects)) {
      await exportObjects(selectedObjects);
    } else {
      await downloadObjects(selectedObjects);
    }
  };

  const forgeActionItems = [
    {
      id: "EDIT",
      group: "edit",
      name: "Edit in Canvas",
      icon: <CanvasIcon />,
      colors: "secondary.1",
      onClick: () =>
        editInCanvas({
          object: objects[0] as EditInCanvasObject,
          sessionId,
          navigateOptions: { replace: true },
        }),
    },
    {
      id: "VARIATION",
      group: "forge",
      name: "Create Image",
      icon: <TwoDIcon />,
      colors: "secondary.1",
      onClick: () =>
        activateInferenceForm("CREATE", [singleFileOrAsset as any]),
    },
    {
      id: "CREATE_VIDEO",
      group: "forge",
      name: "Create Video",
      icon: <VideoIcon />,
      colors: "secondary.1",
      onClick: () =>
        singleFileOrAsset &&
        activateInferenceForm("CREATE_VIDEO", [singleFileOrAsset as any]),
    },
    {
      id: "CREATE_3D",
      group: "forge",
      name: "Create 3D Model",
      icon: <ThreeDIcon />,
      colors: "secondary.1",
      onClick: () =>
        singleFileOrAsset &&
        activateInferenceForm("CREATE_3_D", [singleFileOrAsset as any]),
    },
    {
      id: "REFORGE",
      group: "edit",
      name: `Re-forge ${capitalize(itemType)}`,
      icon: <Icon icon={faArrowsRetweet} className="fa fa-fw" />,
      colors: "secondary.1",
      onClick: () => singleFile && activateReforgeForm(singleFile),
    },
  ].filter((item) => availableActionIds.has(item.id as ActionId));

  const refineActionItem = [
    {
      id: "PROMPT_EDIT_IMAGE",
      group: "session",
      name: "Prompt Edit",
      icon: <PromptEditIcon />,
      colors: "secondary.1",
      onClick: () => activateInferenceForm("PROMPT_EDIT_IMAGE", filesAndAssets),
    },
    {
      id: "UPSCALE",
      group: "session",
      name: "Upscale",
      icon: <UpscaleImageIcon />,
      colors: "secondary.1",
      onClick: () => activateInferenceForm("UPSCALE", filesAndAssets),
    },
    {
      id: "REFRAME_IMAGE",
      group: "session",
      name: "Reframe",
      icon: <ReframeIcon />,
      colors: "secondary.1",
      onClick: () => activateInferenceForm("REFRAME_IMAGE", filesAndAssets),
    },
    {
      id: "VECTORIZE_IMAGE",
      group: "session",
      name: "Vectorize",
      icon: <VectorizeIcon />,
      colors: "secondary.1",
      onClick: () => activateInferenceForm("VECTORIZE_IMAGE", filesAndAssets),
    },
    {
      id: "REMOVE_BACKGROUND",
      group: "session",
      name: "Remove Background",
      icon: <RemoveBackgroundIcon />,
      colors: "secondary.1",
      onClick: () => activateInferenceForm("REMOVE_BACKGROUND", filesAndAssets),
    },
    {
      id: "PROMPT_EDIT_VIDEO",
      group: "session",
      name: "Prompt Edit",
      icon: <PromptEditIcon />,
      colors: "secondary.1",
      onClick: () =>
        singleFile && activateInferenceForm("PROMPT_EDIT_VIDEO", [singleFile]),
    },
    {
      id: "UPSCALE_VIDEO",
      group: "session",
      name: "Upscale Video",
      icon: <UpscaleVideoIcon />,
      colors: "secondary.1",
      onClick: () =>
        singleFile && activateInferenceForm("UPSCALE_VIDEO", [singleFile]),
    },
    {
      id: "REFRAME_VIDEO",
      group: "session",
      name: "Reframe",
      icon: <ReframeIcon />,
      colors: "secondary.1",
      onClick: () =>
        singleFile && activateInferenceForm("REFRAME_VIDEO", [singleFile]),
    },
    {
      id: "REMOVE_BACKGROUND_VIDEO",
      group: "session",
      name: "Remove Background",
      icon: <RemoveBackgroundIcon />,
      colors: "secondary.1",
      onClick: () =>
        singleFile &&
        activateInferenceForm("REMOVE_BACKGROUND_VIDEO", [singleFile]),
    },
    {
      id: "ADD_SFX_VIDEO",
      group: "session",
      name: "Add Sound Effects",
      icon: <AudioIcon />,
      colors: "secondary.1",
      onClick: () =>
        singleFile && activateInferenceForm("ADD_SFX_VIDEO", [singleFile]),
    },
    {
      id: "RETEXTURE_MESH",
      group: "session",
      name: "Retexture",
      icon: <Icon icon={faPaintbrush} size="large" />,
      colors: "secondary.1",
      onClick: () =>
        singleFile && activateInferenceForm("RETEXTURE_MESH", [singleFile]),
    },
    {
      id: "RIG_MESH",
      group: "session",
      name: "Rig",
      icon: <Icon icon={faPersonRunning} size="large" />,
      colors: "secondary.1",
      onClick: () =>
        singleFile && activateInferenceForm("RIG_MESH", [singleFile]),
    },
    {
      id: "REMESH",
      group: "session",
      name: "Remesh",
      icon: <Icon icon={faBlockBrick} size="large" />,
      colors: "secondary.1",
      onClick: () =>
        singleFile && activateInferenceForm("REMESH", [singleFile]),
    },
  ].filter(
    (item) =>
      availableActionIds.has(item.id as ActionId) && item.id !== "RIG_MESH",
  ); // TODO: Remove this once AnythingWorld fixes their endpoint

  const exportActionItems = [
    {
      id: "COPY_TO_CLIPBOARD",
      group: "export",
      name: "Copy to Clipboard",
      icon: <Icon icon={faClipboard} className="fa-fw" />,
      onClick: () => copyFilesToClipboard(filesAndAssets),
    },
    {
      id: "COPY_LINK",
      group: "export",
      name: "Copy Link to File",
      icon: <Icon icon={faGlobe} className="fa-fw" />,
      onClick: () => copyFileLink(filesAndAssets[0]),
    },
    {
      id: "EXPORT",
      group: "export",
      name: "Export",
      icon: <Icon icon={faArrowDownToLine} className="fa-fw" />,
      colors: "secondary.1",
      subMenu: [
        {
          id: "SAVE_TO_DRIVE",
          name: "Save to Drive",
          icon: <Icon icon={faCirclePlus} className="fa-fw" />,
          onClick: () => setIsDriveManagerDialogOpen(true),
        },
        {
          id: "DOWNLOAD_FILES",
          name: "Download",
          icon: <Icon icon={faArrowDownToLine} className="fa-fw" />,
          onClick: () => downloadObjects(filesAndAssets),
        },
        {
          id: "DOWNLOAD_AS_OBJ",
          name: "Download as OBJ",
          icon: <Icon icon={faCube} className="fa-fw" />,
          onClick: () => downloadFilesAsObj(allFiles),
        },
      ].filter((item) =>
        availableExportOptionIds.has(item.id as ExportOptionId),
      ),
    },
    {
      id: "QUICK_DOWNLOAD",
      group: "export",
      name: "Quick Download",
      icon: <Icon icon={faArrowDownToLine} className="fa-fw" />,
      colors: "secondary.1",
      onClick: () => handleDownloadFiles(filesAndAssets),
    },
    {
      id: "DOWNLOAD_AS",
      group: "export",
      name: "Download as...",
      icon: <Icon icon={faLaptopFile} className="fa-fw" />,
      colors: "secondary.1",
      onClick: () => setIsExportDialogOpen(true),
    },
    {
      id: "SAVE_TO_DRIVE",
      group: "export",
      name: "Save to Drive",
      icon: <Icon icon={faFolderArrowDown} className="fa-fw" />,
      onClick: () => setIsDriveManagerDialogOpen(true),
    },
  ].filter((item) => {
    if (["QUICK_DOWNLOAD", "SAVE_TO_DRIVE"].includes(item.id)) {
      return availableActionIds.has("EXPORT");
    }
    if (item.id === "DOWNLOAD_AS") {
      return (
        availableActionIds.has("EXPORT") &&
        (allFiles.every((file) => isRasterImage(file)) ||
          allFiles.every((file) => isVideo(file)))
      );
    }
    if (["COPY_TO_CLIPBOARD", "COPY_LINK"].includes(item.id)) {
      return availableActionIds.has(item.id as ActionId);
    }
    return false;
  });

  return (
    <>
      <Stack
        direction="column"
        sx={{
          gap: 1,
        }}
      >
        {forgeActionItems.length > 0 && (
          <ButtonGroup orientation="vertical">
            {forgeActionItems.map((item) => (
              <SelectionSidebarMainActionButton
                item={item as SelectionMenuItem}
                key={item?.id}
              />
            ))}
          </ButtonGroup>
        )}
        {refineActionItem.length > 0 && (
          <ButtonGroup orientation="vertical">
            {refineActionItem.map((item) => (
              <SelectionSidebarMainActionButton
                item={item as SelectionMenuItem}
                key={item?.id}
              />
            ))}
          </ButtonGroup>
        )}
        {exportActionItems.length > 0 && (
          <ButtonGroup orientation="vertical">
            {exportActionItems.map((item) => (
              <SelectionSidebarMainActionButton
                item={item as SelectionMenuItem}
                key={item?.id}
              />
            ))}
          </ButtonGroup>
        )}
      </Stack>
      <DriveManagerDialog
        isOpen={isDriveManagerDialogOpen}
        onClose={() => setIsDriveManagerDialogOpen(false)}
        selectableItemTypes={["Directory"]}
        selectableItemCount={1}
        onConfirmDirectorySelection={(directoryPath) =>
          saveToDrive(objects, directoryPath)
        }
        confirmText="Export"
      />
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        objects={objects}
      />
    </>
  );
};
